// Always-mounted host (rendered once in App.js) that DRIVES every in-progress
// RFP analysis job to completion, independent of which page the user is on.
//
// ProjectPage only starts a job (startJob) and POSTs nothing itself; this host
// owns the whole lifecycle: it issues the extract/summary/alignment requests,
// polls for completion, advances the job's phase, and — when the job finishes
// while the user is NOT on that folder's tab — fires a clickable toast that
// navigates to the results. Because this component lives in App.js it never
// unmounts during in-app navigation, so its async drivers keep running when the
// user leaves the project page. Jobs are also persisted (see utils/analysisJobs)
// so a full page reload re-attaches drivers and resumes from where they left off
// (the `*Posted` flags prevent re-issuing requests that already fired).

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import api from "../pages/chat/api";
import {
  subscribe,
  getAllJobs,
  getJob,
  updateJob,
  endJob,
  markCompleted,
  tryClaim,
  refreshClaim,
} from "../utils/analysisJobs";

const EXTRACT_POLL_MS = 3000;
const EXTRACT_MAX_POLLS = 100; // ~5 min safety cap
const ALIGN_POLL_MS = 2500;
const ALIGN_MAX_POLLS = 140; // ~6 min safety cap
const IN_FLIGHT = ["pending", "processing", "not_started"];
const JOB_MAX_AGE_MS = 45 * 60 * 1000; // drop orphaned jobs after 45 min
const CLAIM_TTL_MS = 25000; // a claim older than this is takeover-eligible
const CLAIM_HEARTBEAT_MS = 8000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const authHeaders = () => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

const filesUrl = (job) =>
  job.orgId
    ? `api/v1/rfp/folders/${job.folderId}/files/?organization_id=${encodeURIComponent(job.orgId)}`
    : `api/v1/rfp/folders/${job.folderId}/files/`;
const alignmentUrl = (job) => {
  const orgId = job.orgId || Cookies.get("selected_org_id");
  return orgId
    ? `api/v1/rfp/folders/${job.folderId}/requirements/alignment/?organization_id=${encodeURIComponent(orgId)}`
    : `api/v1/rfp/folders/${job.folderId}/requirements/alignment/`;
};
const extractUrl = (job, scope) =>
  `api/v1/rfp/folders/${job.folderId}/insights/extract/?insights=${scope}`;
const summaryUrl = (job) => `api/v1/rfp/folders/${job.folderId}/analysis-summary/`;

// Screening lives inside the Requirements tab, so it routes there too.
const tabSegment = (kind) =>
  kind === "requirements" || kind === "screening" ? "requirements" : "bid-readiness";
const tabUrl = (job) =>
  job.orgId
    ? `/rfp-projects/project/${job.folderId}/org/${job.orgId}/${tabSegment(job.kind)}`
    : `/rfp-projects/project/${job.folderId}/${tabSegment(job.kind)}`;

// Is the user currently looking at this exact folder + tab? The trailing slash
// matters: without it, folder "16" would match "/project/160/..." (prefix).
const isOnJobTab = (pathname, job) =>
  pathname.includes(`/project/${job.folderId}/`) && pathname.endsWith(tabSegment(job.kind));

const isActive = (job) => getJob(job.folderId, job.kind) !== null;

export default function AnalysisWatcherHost() {
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the latest pathname readable from inside long-running async drivers
  // without re-subscribing them on every navigation.
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  // Keys ("folderId:kind") with a driver currently running — guards against
  // starting a second driver for the same job on each store emission.
  const runningRef = useRef(new Set());

  useEffect(() => {
    // --- request helpers (read freshest job from the store each step) ---
    const readJob = (job) => getJob(job.folderId, job.kind) || job;

    // `docKeys` restricts the run to those documents (the per-file Resume);
    // otherwise `skipExtracted` leaves documents whose requirements are complete.
    const postExtract = async (job, scope, skipExtracted = false, docKeys = null) => {
      let url = extractUrl(job, scope);
      if (docKeys?.length) url += docKeys.map((k) => `&doc_keys=${encodeURIComponent(k)}`).join("");
      else if (skipExtracted) url += "&skip_extracted=1";
      const res = await api.post(url, {}, authHeaders());
      if (![200, 202].includes(res.status)) throw new Error("extract failed");
      const enqueued = res.data?.enqueued || [];
      const skipped = res.data?.skipped || [];
      return { keys: enqueued.map((e) => e.pdf_key).filter(Boolean), skipped };
    };

    const getTotalRequirements = async (job) => {
      try {
        const res = await api.get(alignmentUrl(job));
        return res.data?.total_requirements ?? 0;
      } catch {
        return 0;
      }
    };

    const folderHasDates = async (job) => {
      try {
        const res = await api.get(filesUrl(job));
        return (res.data?.documents || []).some((d) => (d.insights?.dates || []).length > 0);
      } catch {
        return false;
      }
    };

    // Classify a 0-enqueued extract: docs still ingesting vs. nothing analyzable.
    const notReadyStatus = (skipped) => {
      const reasons = (skipped || []).map((s) => (s.reason || "").toLowerCase());
      return reasons.some((r) => r.includes("not ingested")) ? "ingesting" : "no_docs";
    };

    // Poll `files` until the watched docs leave an in-flight insights_status.
    // 'not_started' counts as in-flight (the files response is cached and the
    // extract endpoint doesn't bust it). We require having SEEN them in-flight
    // before trusting a terminal read, falling back to the poll cap otherwise.
    const waitForDateExtraction = async (job, watchedKeys) => {
      const watched = new Set(watchedKeys);
      if (watched.size === 0) return;
      let seen = false;
      for (let polls = 0; polls < EXTRACT_MAX_POLLS; polls += 1) {
        await sleep(EXTRACT_POLL_MS);
        if (!isActive(job)) return;
        try {
          const res = await api.get(filesUrl(job));
          const docs = res.data?.documents || [];
          const watchedDocs = docs.filter((d) => watched.has(d.key));
          const inFlight = watchedDocs.filter((d) => IN_FLIGHT.includes(d.insights_status)).length;
          if (inFlight > 0) seen = true;
          // Fresh docs: wait until we saw them running and then settle.
          if (seen && inFlight === 0) return;
          // Re-run on docs that already had insights: the files endpoint reports
          // a pre-masked "completed" and never shows in-flight. After a few stable
          // terminal polls, treat it as done instead of waiting out the full cap.
          if (!seen && inFlight === 0 && watchedDocs.length > 0 && polls >= 4) return;
        } catch {
          /* transient — keep polling */
        }
      }
    };

    const postAlignment = async (job) => {
      await api.post(alignmentUrl(job), {}, authHeaders());
    };

    // Poll alignment status until terminal. Right after POST the task may still
    // report a stale terminal status, so we ride a short grace window until it
    // is confirmed running.
    //
    // `extraction_pending` is the server holding alignment until every document
    // has extracted — which on many or large files takes far longer than the
    // scoring itself (a large file has a 40-minute budget). The server starts
    // alignment on its own when the last one lands, so we wait it out for as
    // long as the job itself may live; only the scoring has the short cap.
    const waitForAlignment = async (job) => {
      let seenRunning = false;
      let polls = 0;
      while (polls < ALIGN_MAX_POLLS) {
        await sleep(ALIGN_POLL_MS);
        if (!isActive(job)) return { status: "cancelled" };
        try {
          const res = await api.get(alignmentUrl(job));
          const s = res.data?.alignment_status || res.data?.status;
          if (s === "extraction_pending") {
            if (Date.now() - (readJob(job).startedAt || 0) > JOB_MAX_AGE_MS) return { status: "failed" };
            continue;
          }
          polls += 1;
          if (s === "running") {
            seenRunning = true;
            continue;
          }
          if (s === "done" || s === "failed") return { status: s };
          if (!seenRunning && polls < 5) continue; // grace: task not running yet
          if (s === "not_started") return { status: "failed" };
        } catch {
          polls += 1; // transient — keep polling
        }
      }
      return { status: "failed" };
    };

    // --- per-kind drivers ---
    const driveBidReadiness = async (job) => {
      // 1. Project Overview summary (best-effort — don't block extraction on it).
      //    Time-boxed so a slow/hung summary endpoint can't stall the pipeline.
      if (!readJob(job).summaryPosted) {
        updateJob(job.folderId, job.kind, { phase: "summarizing", summaryPosted: true });
        try {
          await api.post(summaryUrl(job), {}, { ...authHeaders(), timeout: 90000 });
        } catch {
          /* summary is non-critical; continue to extraction */
        }
      }
      if (!isActive(job)) return { status: "cancelled" };

      if (await folderHasDates(job)) return { status: "done" };

      let watched = readJob(job).watchedKeys || [];
      if (!readJob(job).extractPosted) {
        const { keys, skipped } = await postExtract(job, "date");
        watched = keys;
        updateJob(job.folderId, job.kind, {
          phase: "extracting",
          extractPosted: true,
          watchedKeys: watched,
        });
        if (keys.length === 0) return { status: notReadyStatus(skipped) };
      }
      await waitForDateExtraction(job, watched);
      return { status: "done" };
    };

    const driveRequirements = async (job) => {
      // 1. Requirements extraction. A re-run extracts only the documents whose
      //    requirements are not complete — new files, and ones whose last run
      //    stopped short (those resume where they stopped). `extractKeys` is the
      //    per-file Resume: just those documents. (`alignOnly` is the old name
      //    of `extractNewOnly`, kept for jobs started before the rename.)
      if (!readJob(job).extractPosted) {
        const newOnly = !!(readJob(job).extractNewOnly ?? readJob(job).alignOnly);
        const { keys, skipped } = await postExtract(job, "requirements", newOnly, readJob(job).extractKeys);
        updateJob(job.folderId, job.kind, { phase: "extracting", extractPosted: true });
        const stillExtracting = skipped.some((s) => s.reason === "already processing");
        if (keys.length === 0 && !stillExtracting && (await getTotalRequirements(job)) === 0) {
          // Nothing to extract and nothing already extracted to align.
          return { status: notReadyStatus(skipped) };
        }
      }

      // 2. Alignment — asked for straight away, not after guessing when
      //    extraction is over. While any document is still extracting the
      //    server parks it and starts it itself once the last one lands.
      if (!readJob(job).alignPosted) {
        updateJob(job.folderId, job.kind, { phase: "aligning", alignPosted: true });
        await postAlignment(job);
      }
      return waitForAlignment(job);
    };

    // Poll the alignment GET (which carries `screening_status` + answers) until
    // screening finishes. Mirrors waitForAlignment with a short grace window.
    const waitForScreening = async (job) => {
      let seenRunning = false;
      for (let polls = 0; polls < ALIGN_MAX_POLLS; polls += 1) {
        await sleep(ALIGN_POLL_MS);
        if (!isActive(job)) return { status: "cancelled" };
        try {
          const res = await api.get(alignmentUrl(job));
          const s = res.data?.screening_status;
          if (s === "running") {
            seenRunning = true;
            continue;
          }
          if (s === "done" || s === "failed") return { status: s };
          if (!seenRunning && polls < 5) continue; // grace: task not running yet
          if (s === "not_started") return { status: "failed" };
        } catch {
          /* transient — keep polling */
        }
      }
      return { status: "failed" };
    };

    const driveScreening = async (job) => {
      if (!readJob(job).screeningPosted) {
        updateJob(job.folderId, job.kind, { phase: "screening", screeningPosted: true });
        const orgId = job.orgId || Cookies.get("selected_org_id");
        await api.post(
          orgId
            ? `api/v1/rfp/folders/${job.folderId}/requirements/screening/?organization_id=${encodeURIComponent(orgId)}`
            : `api/v1/rfp/folders/${job.folderId}/requirements/screening/`,
          {},
          authHeaders()
        );
      }
      return waitForScreening(job);
    };

    const notifyComplete = (job, result) => {
      const label =
        job.kind === "requirements"
          ? "Requirement analysis"
          : job.kind === "screening"
          ? "Screening answers"
          : "Bid Readiness analysis";
      const st = result?.status;

      // Non-completion outcomes: nothing was produced, so don't offer "View results".
      if (st === "ingesting") {
        toast(`${label}: documents are still processing. Run analysis again once they finish.`, {
          duration: 6000,
          icon: "⏳",
        });
        return;
      }
      if (st === "no_docs") {
        toast(`${label}: no analyzable documents found.`, { duration: 6000, icon: "⚠️" });
        return;
      }

      const onTab = isOnJobTab(pathnameRef.current, job);
      const failed = st === "failed";

      // On the relevant tab: the page renders results itself. Only surface a
      // toast for failures (so the user isn't left staring at a reset screen).
      if (onTab && !failed) return;

      toast(
        (t) => (
          <span
            role="button"
            tabIndex={0}
            style={{ cursor: "pointer" }}
            onClick={() => {
              toast.dismiss(t.id);
              navigate(tabUrl(job));
            }}
          >
            {failed ? `${label} finished with no results` : `${label} complete`}
            {" — "}
            <span style={{ textDecoration: "underline", fontWeight: 600 }}>View results</span>
          </span>
        ),
        { duration: 6000, icon: failed ? "⚠️" : "✅" }
      );
    };

    const runDriver = async (job) => {
      let result = { status: "failed" };
      try {
        result =
          job.kind === "requirements"
            ? await driveRequirements(job)
            : job.kind === "screening"
            ? await driveScreening(job)
            : await driveBidReadiness(job);
      } catch (e) {
        // Network/permission failure starting the pipeline — surface and clear.
        if (isActive(job)) {
          endJob(job.folderId, job.kind);
          const what =
            job.kind === "requirements"
              ? "Requirement analysis"
              : job.kind === "screening"
              ? "Screening answer generation"
              : "Bid Readiness analysis";
          // Prefer the backend's reason (e.g. "No selected screening questions
          // found", "No ingested documents") over a generic message.
          const detail = e?.response?.data?.error || e?.response?.data?.message;
          toast.error(detail ? `${what}: ${detail}` : `${what} couldn't start.`);
        }
        return;
      }
      if (result.status === "cancelled" || !isActive(job)) return; // user cancelled
      // Sticky "this flow ran" marker so the tab shows its results view on return
      // (the shared insights_status can't tell which flow ran). Only on a real
      // completion — not on "ingesting"/"no_docs"/"failed", which produced nothing.
      if (result.status === "done") {
        markCompleted(job.folderId, job.kind);
      }
      endJob(job.folderId, job.kind);
      notifyComplete(job, result);
    };

    const ensureDrivers = () => {
      getAllJobs().forEach((job) => {
        const key = `${job.folderId}:${job.kind}`;
        if (runningRef.current.has(key)) return;
        // Drop orphaned/stale jobs (browser closed mid-run, folder deleted, etc.)
        // so they don't poll for minutes and fire a misleading toast.
        if (Date.now() - (job.startedAt || 0) > JOB_MAX_AGE_MS) {
          endJob(job.folderId, job.kind);
          return;
        }
        // Reserve locally BEFORE claiming: tryClaim emits, which re-enters this
        // function — having the key already present makes that re-entry a no-op.
        runningRef.current.add(key);
        if (!tryClaim(job.folderId, job.kind, CLAIM_TTL_MS)) {
          runningRef.current.delete(key); // another live tab owns this job
          return;
        }
        const heartbeat = setInterval(
          () => refreshClaim(job.folderId, job.kind),
          CLAIM_HEARTBEAT_MS
        );
        runDriver(job).finally(() => {
          clearInterval(heartbeat);
          runningRef.current.delete(key);
        });
      });
    };

    ensureDrivers(); // attach drivers for jobs already present (e.g. after reload)
    const unsub = subscribe(ensureDrivers);
    // Periodic re-check makes the system self-healing: it picks up jobs whose
    // owning tab was closed (claim expired), re-attaches a driver that died
    // without cleanup, and drops aged-out jobs — even with no store activity.
    const tick = setInterval(ensureDrivers, 10000);
    return () => {
      unsub();
      clearInterval(tick);
    };
    // navigate is stable; pathname is read via ref so this runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
